class OrderSampleWidget {
  constructor(container) {
    this.container = container;
    this.trigger = container.querySelector('[data-sample-trigger]');
    this.modal = container.querySelector('[data-sample-modal]');
    this.dialog = this.modal?.querySelector('.order-sample__dialog');
    this.optionButtons = Array.from(container.querySelectorAll('[data-sample-option]'));
    this.dismissControls = Array.from(container.querySelectorAll('[data-sample-dismiss]'));
    this.errorMessage = container.querySelector('[data-sample-error]');
    this.loadingTargets = [this.trigger, ...this.optionButtons];

    this.sampleVariants = this.parseVariants();
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.stopDialogClickPropagation = this.stopDialogClickPropagation.bind(this);

    this.bindEvents();
  }

  parseVariants() {
    const data = this.container.querySelector('[data-sample-variants]');

    if (!data) return [];

    try {
      const parsed = JSON.parse(data.textContent || '{}');
      return Array.isArray(parsed.variants) ? parsed.variants : [];
    } catch (error) {
      console.error('Unable to parse sample variants', error);
      return [];
    }
  }

  bindEvents() {
    if (!this.trigger || !this.sampleVariants.length) return;

    this.trigger.addEventListener('click', this.handleTriggerClick);
    this.optionButtons.forEach((button) =>
      button.addEventListener('click', () => this.addSampleToCart(button.dataset.variantId))
    );
    this.dismissControls.forEach((control) =>
      control.addEventListener('click', () => this.closeModal())
    );

    if (this.modal) {
      this.modal.addEventListener('click', this.handleModalClick);
    }

    if (this.dialog) {
      this.dialog.addEventListener('click', this.stopDialogClickPropagation);
    }
  }

  handleTriggerClick() {
    if (this.sampleVariants.length === 1 || !this.modal) {
      this.addSampleToCart(this.sampleVariants[0].id);
      return;
    }

    this.openModal();
  }

  handleKeyup(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }

  handleModalClick(event) {
    if (!this.modal || this.modal.hidden) return;

    const clickedElement = event.target instanceof Element ? event.target : null;
    if (clickedElement && clickedElement.closest('.order-sample__dialog')) return;

    this.closeModal();
  }

  stopDialogClickPropagation(event) {
    event.stopPropagation();
  }

  setLoading(isLoading, target = this.trigger) {
    if (!target) return;

    target.classList.toggle('loading', isLoading);
    target.setAttribute('aria-disabled', isLoading);
    target.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
  }

  showError(message) {
    if (!this.errorMessage) return;

    this.errorMessage.textContent = message;
    this.errorMessage.hidden = !message;
  }

  addSampleToCart(variantId) {
    if (!variantId || typeof fetchConfig !== 'function') return;

    window.pmAnalytics?.track('order_sample_add_to_cart_attempt', {
      product_id: this.container.closest('[data-product-id]')?.dataset.productId,
      sample_variant_id: variantId,
    });

    const sampleAddErrorMessage = window.productStrings?.sampleAddError || window.cartStrings?.error || '';

    this.showError('');
    this.loadingTargets.forEach((target) => this.setLoading(true, target));

    const config = fetchConfig('json');
    config.headers['Content-Type'] = 'application/json';

    const sections = this.cart?.getSectionsToRender?.().map((section) => section.id);

    config.body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections,
      sections_url: window.location.pathname,
    });

    this.cart?.setActiveElement?.(document.activeElement);

    fetch(`${routes.cart_add_url}`, config)
      .then(async (response) => {
        const responseText = await response.text();
        let parsedResponse = null;

        try {
          parsedResponse = responseText ? JSON.parse(responseText) : null;
        } catch (error) {
          throw new Error(sampleAddErrorMessage);
        }

        if (!response.ok || !parsedResponse) {
          const message =
            parsedResponse?.description || parsedResponse?.message || sampleAddErrorMessage;
          throw new Error(message);
        }

        return parsedResponse;
      })
      .then((response) => {
        if (response.status) {
          const message = response.description || response.message || sampleAddErrorMessage;
          this.showError(message);
          this.setCartError(message);
          return;
        }

        if (typeof renderCartAddResponse === 'function') {
          renderCartAddResponse(this.cart, response, variantId, 'order-sample');
        } else if (typeof this.cart?.onCartUpdate === 'function') {
          this.cart.onCartUpdate();
        } else {
          window.location = window.routes.cart_url;
        }

        window.pmAnalytics?.track('order_sample_added_to_cart', {
          product_id: this.container.closest('[data-product-id]')?.dataset.productId,
          sample_variant_id: variantId,
        });

        this.closeModal();
      })
      .catch((error) => {
        console.error('[OrderSample] Failed to add sample to cart', error);
        const message = error instanceof Error ? error.message : sampleAddErrorMessage;
        this.showError(message);
        this.setCartError(message);
      })
      .finally(() => {
        this.loadingTargets.forEach((target) => this.setLoading(false, target));
      });
  }

  setCartError(message) {
    const errorContainer = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (!errorContainer) return;
    errorContainer.textContent = message || window.productStrings?.sampleUpdateError || window.cartStrings?.error || '';
  }

  openModal() {
    if (!this.modal) return;

    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    document.addEventListener('keyup', this.handleKeyup);
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keyup', this.handleKeyup);
  }
}

const initializeOrderSampleWidgets = (scope = document) => {
  scope.querySelectorAll('[data-order-sample]').forEach((container) => {
    if (container.__orderSampleWidgetInitialized) return;
    container.__orderSampleWidgetInitialized = true;
    new OrderSampleWidget(container);
  });
};

document.addEventListener('DOMContentLoaded', () => initializeOrderSampleWidgets());

document.addEventListener('product-info:loaded', ({ target }) => {
  initializeOrderSampleWidgets(target);
});
